import Phaser from 'phaser';
import { ALL_ANIMAL_KEYS, ANIMAL_STATS } from '../config/animals';
import { GameState } from '../state/GameState';
import { SaveStore } from '../state/SaveStore';
import { makeMountWithRider } from '../render/mount';
import type { AnimalKey } from '../types';

export class AnimalSelectorScene extends Phaser.Scene {
  private idx = 0;
  private displayContainer!: Phaser.GameObjects.Container;
  private nameText!: Phaser.GameObjects.Text;
  private flavorText!: Phaser.GameObjects.Text;
  private statTexts: Phaser.GameObjects.Text[] = [];

  constructor() {
    super('AnimalSelectorScene');
  }

  create(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    this.add.rectangle(0, 0, W, H, 0xd6f2ea).setOrigin(0, 0);
    this.add
      .text(W / 2, 50, 'Pick Your Mount', {
        fontFamily: 'sans-serif',
        fontSize: '40px',
        color: '#1d3a44',
      })
      .setOrigin(0.5);

    this.displayContainer = this.add.container(W / 2, H / 2 - 20);

    this.nameText = this.add
      .text(W / 2, H / 2 + 110, '', {
        fontFamily: 'sans-serif',
        fontSize: '32px',
        color: '#1d3a44',
      })
      .setOrigin(0.5);
    this.flavorText = this.add
      .text(W / 2, H / 2 + 150, '', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#3d5a64',
      })
      .setOrigin(0.5);

    // Stats column
    const statY = H / 2 - 100;
    const statX = 80;
    this.add.text(statX, statY - 30, 'STATS', {
      fontFamily: 'sans-serif',
      fontSize: '18px',
      color: '#1d3a44',
    });
    for (let i = 0; i < 4; i++) {
      const t = this.add.text(statX, statY + i * 26, '', {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#1d3a44',
      });
      this.statTexts.push(t);
    }

    // Arrows
    this.makeArrow(W / 2 - 240, H / 2, '◀', () => {
      this.idx = (this.idx - 1 + ALL_ANIMAL_KEYS.length) % ALL_ANIMAL_KEYS.length;
      this.refresh();
    });
    this.makeArrow(W / 2 + 240, H / 2, '▶', () => {
      this.idx = (this.idx + 1) % ALL_ANIMAL_KEYS.length;
      this.refresh();
    });

    // Back button (left)
    const back = this.add.container(W / 2 - 160, H - 60);
    const backBg = this.add.rectangle(0, 0, 130, 50, 0x6b3410).setStrokeStyle(2, 0xffffff);
    const backTxt = this.add
      .text(0, 0, '← Back', { fontFamily: 'sans-serif', fontSize: '20px', color: '#fff' })
      .setOrigin(0.5);
    back.add([backBg, backTxt]);
    back.setSize(130, 50).setInteractive({ useHandCursor: true });
    back.on('pointerover', () => backBg.setFillStyle(0x8a4a1a));
    back.on('pointerout', () => backBg.setFillStyle(0x6b3410));
    back.on('pointerup', () => this.scene.start('CharacterCreatorScene'));

    // Confirm (right)
    const confirm = this.add.container(W / 2 + 90, H - 60);
    const bg = this.add.rectangle(0, 0, 240, 50, 0x1d3a44).setStrokeStyle(2, 0xffffff);
    const txt = this.add
      .text(0, 0, 'Start Adventure!', { fontFamily: 'sans-serif', fontSize: '22px', color: '#fff' })
      .setOrigin(0.5);
    confirm.add([bg, txt]);
    confirm.setSize(240, 50).setInteractive({ useHandCursor: true });
    confirm.on('pointerover', () => bg.setFillStyle(0x35637a));
    confirm.on('pointerout', () => bg.setFillStyle(0x1d3a44));
    confirm.on('pointerup', () => this.confirm());

    this.refresh();
  }

  private refresh(): void {
    this.displayContainer.removeAll(true);
    const animalKey: AnimalKey = ALL_ANIMAL_KEYS[this.idx];
    const character = GameState.getCharacter(this.registry);
    const m = makeMountWithRider(this, animalKey, character, 0, 0, { showRider: false });
    m.setScale(2.4);
    this.displayContainer.add(m);

    const stats = ANIMAL_STATS[animalKey];
    this.nameText.setText(stats.displayName);
    this.flavorText.setText(stats.flavor);
    this.statTexts[0].setText(`Speed:  ${stats.startSpeed}–${stats.maxSpeed}`);
    this.statTexts[1].setText(`Jump:   ${Math.round(-stats.jumpVelocity)}`);
    this.statTexts[2].setText(`HP:     ${stats.hp}`);
    this.statTexts[3].setText(`Habitat: ${stats.habitat}`);
  }

  private confirm(): void {
    const animalKey = ALL_ANIMAL_KEYS[this.idx];
    GameState.setAnimal(this.registry, animalKey);
    GameState.setLevel(this.registry, 1);
    SaveStore.save({
      character: GameState.getCharacter(this.registry),
      animal: animalKey,
      level: 1,
    });
    this.scene.start('LevelIntroScene', { level: 1 });
  }

  private makeArrow(x: number, y: number, glyph: string, onClick: () => void): void {
    const t = this.add
      .text(x, y, glyph, {
        fontFamily: 'sans-serif',
        fontSize: '48px',
        color: '#1d3a44',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    t.on('pointerdown', onClick);
  }
}
